import { useState } from 'react';
import { useGameStore } from '@/state/gameStore';
import { visibleAchievements, ACHIEVEMENTS } from '@/domain/achievements';
import { COSMETICS } from '@/domain/cosmetics';
import { Icon, type IconName } from '@/ui/components/Icon';
import './AchievementsPanel.css';

/**
 * Collapsible achievement-list for the Hero tab. Shows progress bars,
 * unlock-state, and claim buttons. Hidden achievements only surface
 * once they've been unlocked (visibleAchievements helper).
 *
 * Two sections: "Verfügbar zum Einlösen" (unlocked but unclaimed) +
 * "Fortschritt" (in-progress + locked). Sorted so the actionable ones
 * are always at the top.
 */
export function AchievementsPanel() {
  const player = useGameStore((s) => s.player);
  const claim = useGameStore((s) => s.claimAchievement);
  const [open, setOpen] = useState(false);

  if (!player) return null;
  const list = visibleAchievements(player.achievements);
  const state = player.achievements ?? {};
  const unclaimed = list.filter((a) => state[a.id]?.unlockedAt && !state[a.id]?.claimedAt);
  const inProgress = list.filter((a) => !state[a.id]?.unlockedAt || state[a.id]?.claimedAt);
  const totalUnlocked = list.filter((a) => state[a.id]?.unlockedAt).length;

  return (
    <section className="ach-panel card">
      <button
        type="button"
        className="ach-panel__header"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Icon name="sparkles" size={18} />
        <span className="ach-panel__title">Erfolge</span>
        <span className="ach-panel__count">{totalUnlocked} / {list.length}</span>
        {unclaimed.length > 0 && (
          <span className="ach-panel__badge">{unclaimed.length} abholbar</span>
        )}
        <span className="ach-panel__chev">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="ach-panel__body">
          {unclaimed.length > 0 && (
            <>
              <div className="ach-panel__section-title">Abholbar</div>
              {unclaimed.map((def) => (
                <AchievementRow
                  key={def.id}
                  defId={def.id}
                  claimable
                  onClaim={() => claim(def.id)}
                />
              ))}
            </>
          )}
          <div className="ach-panel__section-title">Fortschritt</div>
          {inProgress.map((def) => (
            <AchievementRow key={def.id} defId={def.id} claimable={false} onClaim={() => {}} />
          ))}
        </div>
      )}
    </section>
  );
}

interface RowProps {
  defId: string;
  claimable: boolean;
  onClaim: () => void;
}

function AchievementRow({ defId, claimable, onClaim }: RowProps) {
  const player = useGameStore((s) => s.player);
  const def = ACHIEVEMENTS[defId];
  if (!def || !player) return null;
  const s = player.achievements?.[def.id] ?? { progress: 0 };
  const unlocked = !!s.unlockedAt;
  const claimed = !!s.claimedAt;
  const pct = Math.min(100, (s.progress / def.targetProgress) * 100);
  const iconName = (def.iconKey as IconName) || 'sparkles';

  return (
    <div className={`ach-row ${unlocked ? 'ach-row--unlocked' : ''} ${claimed ? 'ach-row--claimed' : ''}`}>
      <span className="ach-row__icon"><Icon name={iconName} size={20} /></span>
      <div className="ach-row__body">
        <div className="ach-row__name">{def.id}</div>
        <div className="ach-row__bar">
          <div className="ach-row__bar-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="ach-row__meta">
          {s.progress.toLocaleString('de-DE')} / {def.targetProgress.toLocaleString('de-DE')}
          {def.reward.neonGems ? ` · ${def.reward.neonGems} ◆` : ''}
          {def.reward.cosmeticId ? ` · ${COSMETICS[def.reward.cosmeticId]?.id ?? 'cosmetic'}` : ''}
        </div>
      </div>
      {claimable && (
        <button type="button" className="ach-row__claim" onClick={onClaim}>
          Abholen
        </button>
      )}
    </div>
  );
}
